import React, { createContext, useState, useEffect, useContext } from 'react';
import { authAPI } from '../api/api';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data;
      setUser(userData);
      
      // Set language based on user preference if available
      if (userData.preferredLanguage && userData.preferredLanguage !== i18n.language) {
        i18n.changeLanguage(userData.preferredLanguage);
        localStorage.setItem('selectedLanguage', userData.preferredLanguage);
      }
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const { user, token } = response.data;
    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);
    
    // Set language based on user preference if available
    if (user.preferredLanguage && user.preferredLanguage !== i18n.language) {
      i18n.changeLanguage(user.preferredLanguage);
      localStorage.setItem('selectedLanguage', user.preferredLanguage);
    }
    
    return user;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    const { user, token } = response.data;
    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    
    // Update language if user preference changed
    if (updatedUserData.preferredLanguage && updatedUserData.preferredLanguage !== i18n.language) {
      i18n.changeLanguage(updatedUserData.preferredLanguage);
      localStorage.setItem('selectedLanguage', updatedUserData.preferredLanguage);
    }
  };

  const updateUserLanguage = (newLanguage) => {
    setUser(prevUser => ({
      ...prevUser,
      preferredLanguage: newLanguage
    }));
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('selectedLanguage', newLanguage);
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isOwner = () => {
    return user?.role === 'owner';
  };

  const isTeamOwner = () => {
    return user?.role === 'team_owner';
  };

  const isPending = () => {
    return user?.role === 'pending';
  };

  const hasTeam = () => {
    return !!user?.teamId;
  };

  const getOwnedTeamId = () => {
    return user?.teamId || null;
  };

  const forgotPassword = async (email) => {
    return await authAPI.forgotPassword(email);
  };

  const resetPassword = async (token, newPassword) => {
    return await authAPI.resetPassword(token, newPassword);
  };

  const refreshUser = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getMe();
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      // If refresh fails, user might need to re-login
      logout();
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshUser,
    updateUser,
    updateUserLanguage,
    isAuthenticated: !!user,
    hasRole,
    isAdmin,
    isOwner,
    isTeamOwner,
    isPending,
    hasTeam,
    getOwnedTeamId
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
